/**
 * 승인 문구 표 (ADR 0028 · ADR 0025)
 * --------------------------------------------------------------------------
 * 서버는 문장을 보내지 않습니다. 닫힌 코드만 오고 문구는 화면이 고릅니다.
 * `Record<Code, string>`이라 계약에 코드가 하나 늘면 **컴파일이 막습니다** —
 * 새 코드가 문구 없이 화면에 도달하거나 원문 그대로 렌더링되는 일이 없습니다.
 *
 * 여러 코드가 같은 문구를 가리키는 것은 의도입니다. 운영자에게 필요한 것은
 * "무엇을 해야 하는가"이고, 서버 내부의 구분까지 문장으로 옮기면 그 자체가
 * 상태 유추 표면이 됩니다.
 */
import type {
  AIActionChangeField,
  AIActionFailureCode,
  AIActionOutcome,
} from "@k8s-dashboard/contracts";
import type { ApprovalErrorCode } from "./approvalState";

/** 검토 요약의 필드 → 라벨 ID. 값은 계약이 검증한 정수·quantity뿐입니다. */
export const CHANGE_FIELD_MESSAGE: Record<AIActionChangeField, string> = {
  REPLICAS: "approve.field.REPLICAS",
  REVISION: "approve.field.REVISION",
  RESTART_TRIGGER: "approve.field.RESTART_TRIGGER",
  CPU_REQUEST: "approve.field.CPU_REQUEST",
  MEMORY_REQUEST: "approve.field.MEMORY_REQUEST",
  CPU_LIMIT: "approve.field.CPU_LIMIT",
  MEMORY_LIMIT: "approve.field.MEMORY_LIMIT",
};

/** 결과 → 문구 ID. UNKNOWN이 별도 문구인 것이 핵심입니다 — 실패와 다릅니다. */
export const OUTCOME_MESSAGE: Record<AIActionOutcome, string> = {
  EXECUTED: "approve.outcome.EXECUTED",
  FAILED: "approve.outcome.FAILED",
  UNKNOWN: "approve.outcome.UNKNOWN",
};

/** 결정적 실패 사유 → 문구 ID. */
export const FAILURE_MESSAGE: Record<AIActionFailureCode, string> = {
  VALIDATION_REJECTED: "approve.failure.VALIDATION_REJECTED",
  PERMISSION_DENIED: "approve.failure.PERMISSION_DENIED",
  TARGET_CHANGED: "approve.failure.TARGET_CHANGED",
  CONFLICT: "approve.failure.CONFLICT",
  NOT_SUPPORTED: "approve.failure.NOT_SUPPORTED",
  UPSTREAM_ERROR: "approve.failure.UPSTREAM_ERROR",
};

/** 오류 코드 → 문구 ID. 여러 코드가 같은 안내를 가리키는 것은 의도입니다. */
export const APPROVAL_ERROR_MESSAGE: Record<ApprovalErrorCode, string> = {
  action_disabled: "approve.error.disabled",
  action_forbidden: "approve.error.forbidden",
  action_origin_rejected: "approve.error.forbidden",
  action_not_found: "approve.error.gone",
  action_dry_run_required: "approve.error.stale",
  action_dry_run_expired: "approve.error.stale",
  action_dry_run_mismatch: "approve.error.stale",
  action_target_changed: "approve.error.stale",
  action_conflict: "approve.error.stale",
  action_not_supported: "approve.error.unsupported",
  action_idempotency_conflict: "approve.error.stale",
  action_busy: "approve.error.busy",
  action_invalid_request: "approve.error.unavailable",
  action_idempotency_required: "approve.error.unavailable",
  action_store_unavailable: "approve.error.unavailable",
  action_upstream_unavailable: "approve.error.unavailable",
  action_unavailable: "approve.error.unavailable",
};

/**
 * 재시도해도 뜻이 있는 사유인지.
 *
 * 꺼진 배포·권한 없음·사라진 제안은 다시 눌러도 같은 답입니다. 다시 시도 버튼을
 * 그런 자리에 두면 사용자는 같은 실패를 반복하고, 서버는 같은 요청을 다시 셉니다.
 */
export function retryable(code: ApprovalErrorCode): boolean {
  switch (code) {
    case "action_disabled":
    case "action_forbidden":
    case "action_origin_rejected":
    case "action_not_found":
    case "action_not_supported":
      return false;
    default:
      return true;
  }
}
